<script lang="ts">
	// Svelte
	import { getContext } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system
	import { ControlsContainer, Toggle, Button, Input, Text, Token } from '@Shelly/shelly-design-system';
	import { ComponentSize } from 'src/helpers/enums';
	//Helpers
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { scrollToElementInWizard } from 'src/components/Lists/list-helpers';
	//Types
	import type { ScheduleParams, TimespecTypes } from 'src/types/requests/schedules/schedules_g2';
	import { dateLabels, digitFormat } from 'src/helpers/date_helper';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let schedule: ScheduleParams,
		type: TimespecTypes,
		isMobile = false,
		spacingBetweenToggleAndContent = 'm',
		toggleInfoText = '',
		toggleText = '';

	let length = schedule[type].length;
	let startValue = schedule[type].startValue;
	let selected = schedule[type].values;
	let tokenWidth = type == 'months' ? 48 : null;
	let elementRef: HTMLElement;

	const tempSelected = [];
	for (let i = 0; i < selected.length; i++) {
		const selectedValue = selected[i];
		if (selectedValue.includes('-')) {
			const split = selectedValue.split('-'),
				start = Number(split[0]),
				end = Number(split[1]);
			for (let j = start; j <= end; j++) {
				const item = j.toString();
				if (!tempSelected.includes(item)) {
					tempSelected.push(item);
				}
			}
		} else if (!tempSelected.includes(selectedValue)) {
			tempSelected.push(selectedValue);
		}
	}
	selected = tempSelected;

	let items = Array.from(
		{ length },
		(_, i: any) =>
			(i = {
				value: `${i + startValue}`,
				title: type == 'months' ? dateLabels(type)[i] : `${i + startValue}`,
			}),
	);

	const onSelect = (value: string) => {
		const i = selected.indexOf(value);
		if (i >= 0) selected.splice(i, 1);
		else selected.push(value);
		schedule[type].values = selected;
		items = items;
	};

	let selectFrom = startValue.toString();
	let selectTo = (length - (/months|dates/.test(type) ? 0 : 1)).toString();

	const onButtonEvent = (e: string, from = -1, to = 60) => {
		if (e != 'select') selected = [];
		if (e != 'clear') {
			if (from < startValue) from = startValue;
			if (to > length + startValue) to = length + startValue;
			for (let i = from; i < to; i++) {
				if ((e == 'odd' && i % 2 == 0) || (e == 'even' && i % 2 != 0)) {
					continue;
				}
				const item = i.toString();
				if (!selected.includes(item)) {
					selected.push(item);
				}
			}
		}
		schedule[type].values = selected;
		items = items;
	};

	let containersRefs: SvelteStore<{
		wizardContainerRef: HTMLElement;
		scrollContainerRef: HTMLElement;
	}> = getContext('weeklySchedulesContainersRefs');

	$: if (elementRef) {
		scrollToElementInWizard(
			$containersRefs.wizardContainerRef,
			elementRef,
			$containersRefs.scrollContainerRef,
		);
	}
</script>

<Toggle bind:checked={schedule[type].selected} size="l" info={toggleInfoText} id="toggle-knj25rc">
	{toggleText}
</Toggle>

{#if schedule[type].selected}
	<div bind:this={elementRef}>
		<ControlsContainer
			class={classMap({
				'mt-s': spacingBetweenToggleAndContent === ComponentSize.S,
				'mt-m': spacingBetweenToggleAndContent === ComponentSize.M,
			})}
			color={$showNewLayout ? 'color-0' : undefined}
		>
			<svelte:fragment slot="first">
				<div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
					<Button
						class={isMobile ? 'button-width-mobile' : null}
						on:click={() => onButtonEvent('even')}
						size="m"
						variant="secondary"
						>{$format(`schedules_select_even`, {
							default: 'Select even',
						})}</Button
					>
					<Button
						class={isMobile ? 'button-width-mobile' : null}
						on:click={() => onButtonEvent('odd')}
						size="m"
						variant="secondary"
						>{$format(`schedules_select_odd`, {
							default: 'Select odd',
						})}</Button
					>
					{#if !isMobile}
						<span class="vr-m"></span>
					{:else}
						<hr class="width-full" />
					{/if}

					<div class="d-flex align-items-center">
						<Text styleType="controls-small" class="mr-xs flex-shrink-0"
							>{$format(`from`, { default: 'from' })}</Text
						>
						<div class="input-wrapper">
							<Input
								type="number"
								icon={null}
								bind:value={selectFrom}
								id="input-87a50eh"
								aria-label={$format(`from`, { default: 'from' })}
							/>
						</div>
					</div>

					<div class="d-flex align-items-center">
						<Text styleType="controls-small" class="mr-xs flex-shrink-0"
							>{$format(`to`, { default: 'to' })}</Text
						>
						<div class="input-wrapper">
							<Input
								type="number"
								icon={null}
								bind:value={selectTo}
								id="input-hapal58"
								aria-label={$format(`to`, { default: 'to' })}
							/>
						</div>
					</div>
					<Button
						class={isMobile ? 'flex-1' : null}
						on:click={() =>
							onButtonEvent('select', Number(selectFrom), Number(selectTo) + 1)}
						size="m"
						variant="secondary">{$format('select', { default: 'Select' })}</Button
					>
					{#if !isMobile}
						<span class="vr-m"></span>
					{:else}
						<hr class="width-full" />
					{/if}
					<Button
						class={isMobile ? 'width-full' : null}
						on:click={() => onButtonEvent('clear')}
						size="m"
						variant="danger">{$format(`schedules_clear`, { default: 'Clear' })}</Button
					>
				</div>
				<hr class="mt-m mb-m" />
				<div class="d-flex flex-wrap" style="gap: 8px;">
					{#each items as i}
						<Token
							id="token-zpc2n0r"
							type="schedule"
							width={tokenWidth}
							bgColor="--color-1"
							icon="fas fa-check"
							selected={selected.indexOf(i.value) >= 0}
							title={!/months|dates/.test(type) ? digitFormat(i.title) : i.title}
							on:click={() => onSelect(i.value)}
						/>
					{/each}
				</div>
			</svelte:fragment>
		</ControlsContainer>
	</div>
{/if}

<style>
	.input-wrapper {
		width: 40px;
	}

	:global(.button-width-mobile) {
		width: calc(50% - 4px) !important;
	}
</style>
